/**
 * Form submission screen.
 * Submits the form, then polls the status endpoint until VERIFICATION or ERROR.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import axios, { CancelToken } from 'axios'

import ErrorScreen from '../ErrorScreen/ErrorScreen'
import LoadingScreen from '../LoadingScreen/LoadingScreen'
import { Lang } from '../../types/common-types'
import { State } from '../../state-actions-reducers'
import AccountForm from '../../types/account-form.types'
import { SUBMIT_ENDPOINT, SUBMIT_STATUS } from '../../server-parameters'
import { useQueryContext } from '../../pages'
import styles from './ScreenFour.module.scss'
import { PassportType } from '../../enums/passportTypeEnum'
import { OccupationType } from '../../enums/occupationTypeEnum'
import { splitPassportNumber } from '../../components/IdentityExist/IdentityExist'
import { getSavedFormId, getSavedUserId, saveOrderId } from '../../local-storage'

const SERVER_SUCCESS = 'VERIFICATION'
const SERVER_ERROR = 'ERROR'

enum SubmitStatus {
  Submitting = 'SUBMITTING',
  Checking  = 'CHECKING',
  Scheduling = 'SCHEDULING',
  Error     = 'ERROR',
  Success   = 'SUCCESS',
}

interface Props {
  lang: Lang
  accountForm: AccountForm
  cart?: any
  resetState: () => void
  nextStep: () => void
  prevStep: () => void
}

interface RequestArgs {
  payload: ReturnType<typeof preparePayload>
  userId: string | null
  formId: string | null
  cancelToken: CancelToken
  onResponse: (status: string) => void
  type: 'SUBMIT' | 'CHECK'
  query?: string
  cart: any
}

/** Filter additionalLinks by a description label. Returns [''] when the label is absent. */
function filterLinks(descriptions: string[], links: string[], ...labels: string[]): string[] {
  if (!labels.some(l => descriptions.includes(l))) return ['']
  return links.filter((_, i) => labels.includes(descriptions[i]))
}

/** Build a submission-ready copy of the form — pure, no mutations. */
function preparePayload(form: State): Partial<State> {
  const desc  = form.additionalLinksDescription
  const links = form.additionalLinks

  let passportNumber = form.passportNumber.replace('_', '')
  let passportSeries = form.passportSeries

  if (form.citizenship === '643' && Number(form.passportType) === PassportType.Passport) {
    const [series, number] = splitPassportNumber(passportNumber)
    passportSeries = series.replace(' ', '')
    passportNumber = number.replace(' ', '')
  }

  return {
    ...form,
    passportNumber,
    passportSeries,
    isStudent:           Number(form.occupationType) === OccupationType.Studying,
    vk:                  filterLinks(desc, links, 'ВКонтакте', 'VK'),
    instagram:           filterLinks(desc, links, 'Instagram'),
    whatsapp:            form.messengers.filter(e => e.type === 'WhatsApp').map(e => e.link),
    telegram:            form.messengers.filter(e => e.type === 'Telegram').map(e => e.link),
    max:                 form.messengers.filter(e => e.type === 'Max').map(e => e.link),
    viber:               [form.messengers.find(e => e.type === 'Viber')?.link ?? ''],
    youtube:             filterLinks(desc, links, 'YouTube'),
    odnoklassniki:       filterLinks(desc, links, 'Одноклассники', 'OK'),
    facebook:            filterLinks(desc, links, 'Facebook'),
    twitter:             filterLinks(desc, links, 'X (Twitter)'),
    skype:               filterLinks(desc, links, 'Skype'),
    vimeo:               filterLinks(desc, links, 'Vimeo'),
    additionalLinks:     filterLinks(desc, links, 'Другое'),
    accountantPersonEmail: form.accountantPersonEmails[0],
    accountantPersonPhone: form.accountantPersonPhones[0],
  }
}

async function submitRequest({
  payload,
  userId,
  formId,
  cancelToken,
  onResponse,
  type,
  query = '',
}: RequestArgs): Promise<void> {
  try {
    const makeRequest = type === 'SUBMIT'
      ? () => axios.post(
          SUBMIT_ENDPOINT,
          { query, userId, formId, form: { ...payload, userId, formId } },
          { cancelToken }
        )
      : () => axios.post(
          SUBMIT_STATUS,
          { userId, formId },
          { cancelToken }
        )

    const { data } = await makeRequest()

    if (!data?.ok || !data?.data) {
      throw new Error(`Server error: ${data?.message ?? 'no status'}`)
    }
    if (data.access_token) {
      saveOrderId(data.access_token, window.localStorage)
    }
    onResponse(data.data)
  } catch (e) {
    if (axios.isCancel(e)) return
    onResponse(SERVER_ERROR)
  }
}

function scheduleCheck(callback: () => void, delay = 2000): () => void {
  const id = setTimeout(callback, delay)
  return () => clearTimeout(id)
}

export default function ScreenFour({
  lang,
  accountForm,
  cart,
  resetState,
  nextStep,
  prevStep,
}: Props) {
  const { getValues } = useFormContext<State>()
  // Snapshot at mount — avoids re-firing on every watch() reference change
  const payloadRef   = useRef(preparePayload(getValues()))
  const startTimeRef = useRef(Date.now())
  const cancelRef    = useRef(axios.CancelToken.source())

  const userId = getSavedUserId(window.localStorage)
  const formId = getSavedFormId(window.localStorage)
  const query  = useQueryContext()

  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Submitting)

  // Cancel pending requests on unmount
  useEffect(() => () => cancelRef.current.cancel(), [])

  // Register unload handler once form is in-flight
  useEffect(() => {
    if (submitStatus === SubmitStatus.Error || submitStatus === SubmitStatus.Submitting) return
    window.addEventListener('unload', resetState)
    return () => window.removeEventListener('unload', resetState)
  }, [submitStatus])

  // State-machine step
  useEffect(() => {
    if (Date.now() - startTimeRef.current > 60_000) {
      setSubmitStatus(SubmitStatus.Error)
      return
    }

    const cancelToken = cancelRef.current.token

    function onResponse(status: string) {
      switch (status) {
        case SERVER_ERROR:   return setSubmitStatus(SubmitStatus.Error)
        case SERVER_SUCCESS: return setSubmitStatus(SubmitStatus.Success)
        default:             return setSubmitStatus(SubmitStatus.Scheduling)
      }
    }

    switch (submitStatus) {
      case SubmitStatus.Submitting:
        submitRequest({ payload: payloadRef.current, userId, formId, cancelToken, onResponse, type: 'SUBMIT', query, cart })
        return

      case SubmitStatus.Checking:
        submitRequest({ payload: payloadRef.current, userId, formId, cancelToken, onResponse, type: 'CHECK', query, cart })
        return

      case SubmitStatus.Scheduling:
        return scheduleCheck(() => setSubmitStatus(SubmitStatus.Checking))

      case SubmitStatus.Success:
        nextStep()
        return

      case SubmitStatus.Error:
        return
    }
  }, [submitStatus])

  return (
    <main className={styles.main}>
      {submitStatus === SubmitStatus.Error
        ? <ErrorScreen accountForm={accountForm} lang={lang} onError={prevStep} />
        : <LoadingScreen />
      }
    </main>
  )
}
